import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Paper } from '@mui/material';

/**
 * EmailCard component renders a stylish modal for sending documentation via email.
 *
 * @component
 * @param {Object} props
 * @param {string} props.email - Current email value.
 * @param {Function} props.setEmail - Callback to update the email value.
 * @param {Function} props.onSubmit - Callback when sending the email.
 * @param {Function} props.onClose - Callback to close the modal.
 */
const EmailCard = ({ email, setEmail, name, setName, onSubmit, onClose, isLoading }) => {
  const [error, setError] = useState(false);

  // List of generic email domains to block
  const blockedDomains = [
    "gmail.com",
    "yahoo.com",
    "hotmail.com",
    "outlook.com",
    "aol.com",
    "icloud.com",
    "mail.com",
    "protonmail.com"
  ];

  const isBusinessEmail = (email) => {
    const domain = email.split('@')[1];
    if (!domain) return false;
    return !blockedDomains.includes(domain.toLowerCase());
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || !isBusinessEmail(email)) {
      setError(true);
      return;
    }
    setError(false);
    onSubmit();
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        bgcolor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1500,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 500, width: '90%', borderRadius: 3, position: 'relative' }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" textAlign="center">
          Receive Documentation
        </Typography>
        <Typography variant="body2" gutterBottom textAlign="center" sx={{ mb: 2 }}>
          Enter your business email to receive the documentation directly to your inbox.
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            type="text"
            sx={{ mb: 3 }}
          />
          <TextField
            fullWidth
            label="Business Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required
            error={error}
            helperText={error ? 'Please enter a valid business email.' : ''}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoading}

            sx={{
              background: 'linear-gradient(45deg, #064D6F 0%, #2b8cb9 51%, #1BB2F8 100%)',
              color: '#fff',
              fontWeight: 700,
              padding: '10px 0',
              mb: 1,
              '&:hover': { background: 'linear-gradient(45deg, #1BB2F8 0%, #2b8cb9 51%, #064D6F 100%)' },
            }}
          >
            {isLoading ? 'Sending...' : 'Send Email'}
          </Button>

          <Button
            onClick={onClose}
            fullWidth
            variant="outlined"
            sx={{
              color: '#064D6F',
              borderColor: '#064D6F',
              fontWeight: 700,
              padding: '10px 0',
              textTransform: 'none',
              '&:hover': { borderColor: '#1BB2F8', color: '#1BB2F8' },
            }}
          >
            Close
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default EmailCard;
